import { shallowMount } from "@vue/test-utils";
import ConsultaBase from "@/mixins/consulta-base";
import * as ACTIONS from "@/config/constants/action-types";

describe("ConsultaBase Component", () => {
  let Component;
  let wrapper;

  beforeEach(() => {
    Component = {
      render() {},
      mixins: [ConsultaBase]
    };
    wrapper = shallowMount(Component);
    // Mock console.error
    jest.spyOn(console, "error").mockImplementation(() => {});
  });

  afterEach(() => {
    // Restore the original console.error
    console.error.mockRestore();
  });

  describe("Data", () => {
    it("Renders the ConsultaBase component", () => {
      expect(wrapper).toBeDefined();
    });

    it("Should set the default values correctly", () => {
      expect(wrapper.vm.model).toEqual({
        nombre: "nombreConsultaTest",
        apellido: "apellidoConsultaTest",
        tipoDoc: "00",
        numeroDoc: "99888777",
        nacionalidad: "80",
        sexo: "M",
        email: "",
        tipoResidencia: "",
        fechaNacimiento: "01/01/0476",
        celular: {
          area: "11",
          numero: "28756563",
          normalizado: {
            area: "11",
            caracteristica: "3815",
            nro: "3838",
            interno: "",
            valido: true
          }
        },
        captcha: ""
      });
    });
  });

  describe("Computeds", () => {
    it("Should return true when numeroDoc is >= 10000000 regardless of birth date", () => {
      wrapper.vm.model.numeroDoc = 10000000;
      wrapper.vm.model.fechaNacimiento = "01/01/1990";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(true);
    });

    it("Should return true when numeroDoc is well above 10000000", () => {
      wrapper.vm.model.numeroDoc = 99888777;
      wrapper.vm.model.fechaNacimiento = "01/01/2000";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(true);
    });

    it("Should return false when numeroDoc < 10000000 and born after 1954", () => {
      wrapper.vm.model.numeroDoc = 9999999;
      wrapper.vm.model.fechaNacimiento = "01/01/1955";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(false);
    });

    it("Should return false when numeroDoc < 10000000 and born in 1990", () => {
      wrapper.vm.model.numeroDoc = 5000000;
      wrapper.vm.model.fechaNacimiento = "15/06/1990";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(false);
    });

    it("Should return true when numeroDoc < 10000000 and born on 31/12/1954 (boundary date)", () => {
      wrapper.vm.model.numeroDoc = 9999999;
      wrapper.vm.model.fechaNacimiento = "31/12/1954";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(true);
    });

    it("Should return true when numeroDoc < 10000000 and born before 1954", () => {
      wrapper.vm.model.numeroDoc = 1000000;
      wrapper.vm.model.fechaNacimiento = "01/01/1930";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(true);
    });

    it("Should return true when numeroDoc < 10000000 and born exactly on 31/12/1954", () => {
      wrapper.vm.model.numeroDoc = 9999998;
      wrapper.vm.model.fechaNacimiento = "31/12/1954";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(true);
    });

    it("Should return false when numeroDoc is 1 and born in recent year", () => {
      wrapper.vm.model.numeroDoc = 1;
      wrapper.vm.model.fechaNacimiento = "01/01/2000";

      expect(wrapper.vm.validarNumeroDocFechaNacimiento).toBe(false);
    });

    describe("forzarDNE", () => {
      it("Should return false by default when forzarDNE.numeroDoc is not set in state", () => {
        expect(wrapper.vm.forzarDNE).toBe(false);
      });

      it("Should return the numeroDoc value when forzarDNE.numeroDoc exists in state", () => {
        wrapper.vm.$store.state.forzarDNE = { numeroDoc: "12345678" };

        expect(wrapper.vm.forzarDNE).toBe("12345678");
      });

      it("Should return false when forzarDNE key does not exist in state", () => {
        delete wrapper.vm.$store.state.forzarDNE;

        expect(wrapper.vm.forzarDNE).toBe(false);
      });

      it("Should return false when forzarDNE.numeroDoc is explicitly false", () => {
        wrapper.vm.$store.state.forzarDNE = { numeroDoc: false };

        expect(wrapper.vm.forzarDNE).toBe(false);
      });

      it("Should return true when forzarDNE.numeroDoc is true", () => {
        wrapper.vm.$store.state.forzarDNE = { numeroDoc: true };

        expect(wrapper.vm.forzarDNE).toBe(true);
      });

      it("Should return the numeric value when forzarDNE.numeroDoc is a number", () => {
        wrapper.vm.$store.state.forzarDNE = { numeroDoc: 99888777 };

        expect(wrapper.vm.forzarDNE).toBe(99888777);
      });

      it("Should return false when forzarDNE exists but numeroDoc is undefined", () => {
        wrapper.vm.$store.state.forzarDNE = {};

        expect(wrapper.vm.forzarDNE).toBe(false);
      });
    });
  });

  describe("Methods", () => {
    describe("trackClick", () => {
      it("Should dispatch DO_CLICK_TRACKING with the element passed", () => {
        const el = { id: "boton-consultar" };
        wrapper.vm.trackClick(el);
        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_CLICK_TRACKING,
          el
        );
      });

      it("Should dispatch DO_CLICK_TRACKING with any value passed", () => {
        wrapper.vm.trackClick("string-element");
        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_CLICK_TRACKING,
          "string-element"
        );
      });

      it("Should dispatch DO_CLICK_TRACKING with null when called without arguments", () => {
        wrapper.vm.trackClick(null);
        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_CLICK_TRACKING,
          null
        );
      });
    });

    describe("onCaptchaExpired", () => {
      it("Should reset the recaptcha ref", () => {
        const resetMock = jest.fn();
        wrapper.vm.$refs = { recaptcha: { reset: resetMock } };

        wrapper.vm.onCaptchaExpired();

        expect(resetMock).toHaveBeenCalledTimes(1);
      });

      it("Should dispatch DO_IS_LOADING with true", () => {
        wrapper.vm.$refs = { recaptcha: { reset: jest.fn() } };

        wrapper.vm.onCaptchaExpired();

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_IS_LOADING,
          true
        );
      });

      it("Should reset recaptcha before dispatching DO_IS_LOADING", () => {
        const callOrder = [];
        const resetMock = jest.fn(() => callOrder.push("reset"));
        wrapper.vm.$store.dispatch = jest.fn(() => callOrder.push("dispatch"));
        wrapper.vm.$refs = { recaptcha: { reset: resetMock } };

        wrapper.vm.onCaptchaExpired();

        expect(callOrder).toEqual(["reset", "dispatch"]);
      });
    });

    describe("onCaptchaRendered", () => {
      it("Should set recaptchaLoaded to false by default", () => {
        expect(wrapper.vm.recaptchaLoaded).toBe(false);
      });

      it("Should set recaptchaLoaded to true when called", () => {
        wrapper.vm.onCaptchaRendered();

        expect(wrapper.vm.recaptchaLoaded).toBe(true);
      });

      it("Should keep recaptchaLoaded as true when called multiple times", () => {
        wrapper.vm.onCaptchaRendered();
        wrapper.vm.onCaptchaRendered();

        expect(wrapper.vm.recaptchaLoaded).toBe(true);
      });

      it("Should not affect other data properties when called", () => {
        const modelBefore = { ...wrapper.vm.model };

        wrapper.vm.onCaptchaRendered();

        expect(wrapper.vm.model).toEqual(modelBefore);
        expect(wrapper.vm.isProduction).toBe(wrapper.vm.isProduction);
      });
    });
    describe("onCaptchaVerified", () => {
      beforeEach(() => {
        wrapper.vm.$refs = { recaptcha: { reset: jest.fn() } };
      });

      it("Should set model.captcha to the token received", () => {
        wrapper.vm.onCaptchaVerified("testToken123");

        expect(wrapper.vm.model.captcha).toBe("testToken123");
      });

      it("Should reset the recaptcha ref", () => {
        const resetMock = wrapper.vm.$refs.recaptcha.reset;

        wrapper.vm.onCaptchaVerified("testToken123");

        expect(resetMock).toHaveBeenCalledTimes(1);
      });

      it("Should dispatch DO_IS_LOADING with true", () => {
        wrapper.vm.onCaptchaVerified("testToken123");

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_IS_LOADING,
          true
        );
      });

      it("Should dispatch DO_INTERNAL_LINK with 'conocer oferta'", () => {
        wrapper.vm.onCaptchaVerified("testToken123");

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_INTERNAL_LINK,
          "conocer oferta"
        );
      });

      it("Should dispatch DO_CONSULTA with the model", () => {
        wrapper.vm.onCaptchaVerified("testToken123");

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_CONSULTA,
          wrapper.vm.model
        );
      });

      it("Should dispatch DO_CONSULTA with model containing the received token", () => {
        wrapper.vm.onCaptchaVerified("uniqueToken456");

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledWith(
          ACTIONS.DO_CONSULTA,
          expect.objectContaining({ captcha: "uniqueToken456" })
        );
      });

      it("Should dispatch exactly 3 actions", () => {
        wrapper.vm.$store.dispatch = jest.fn();

        wrapper.vm.onCaptchaVerified("testToken123");

        expect(wrapper.vm.$store.dispatch).toHaveBeenCalledTimes(3);
      });

      it("Should execute in order: set captcha, reset, DO_IS_LOADING, DO_INTERNAL_LINK, DO_CONSULTA", () => {
        const callOrder = [];
        const resetMock = jest.fn(() => callOrder.push("reset"));
        wrapper.vm.$refs.recaptcha.reset = resetMock;
        wrapper.vm.$store.dispatch = jest.fn(action => callOrder.push(action));

        wrapper.vm.onCaptchaVerified("testToken123");

        expect(callOrder).toEqual([
          "reset",
          ACTIONS.DO_IS_LOADING,
          ACTIONS.DO_INTERNAL_LINK,
          ACTIONS.DO_CONSULTA
        ]);
      });

      it("Should work with an empty string token", () => {
        wrapper.vm.onCaptchaVerified("");

        expect(wrapper.vm.model.captcha).toBe("");
        expect(wrapper.vm.$refs.recaptcha.reset).toHaveBeenCalledTimes(1);
      });

      it("Should update model.captcha before dispatching DO_CONSULTA", () => {
        let captchaAtDispatch;
        wrapper.vm.$store.dispatch = jest.fn((action, payload) => {
          if (action === ACTIONS.DO_CONSULTA) {
            captchaAtDispatch = payload.captcha;
          }
        });

        wrapper.vm.onCaptchaVerified("lateToken789");

        expect(captchaAtDispatch).toBe("lateToken789");
      });
    });
  });
});
